import React, {useLayoutEffect, useRef, useState} from "react"
import PropTypes from "prop-types"
import {Paper, Box, Typography, IconButton} from "@mui/material"
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded"
import {ReactComponent as CheckIcon} from "../../assets/icons/check-icon.svg"
import theme from "../../theme/theme"

const FormSectionAccordion = (
    {
        title,
        icon,
        children,
        actions,
        defaultOpen = false,
        syncToken = 0,
        isValid,
        showError,
        header,
        disableBorder = false,
        disableIcon = false,
        maxHeight = 2000,
        iconCallbackFunction = null,
        callbackFunction = null
    }) => {
    const [open, setOpen] = useState(defaultOpen)
    const initializedRef = useRef(false)

    useLayoutEffect(() => {
        if (!initializedRef.current) {
            if (defaultOpen) {
                setOpen(true)
            }
            initializedRef.current = true
            return
        }
        setOpen(defaultOpen)
    }, [defaultOpen, syncToken])
    const borderColor = showError
        ? theme.palette.error.main
        : isValid === true
            ? theme.palette.success.main
            : theme.palette.warning.main
    const resolvedIcon = icon || (
        <CheckIcon style={{color: theme.palette.icon.main, width: 20, height: 20}}/>
    )

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                background: theme.palette.background.paper,
                boxShadow: "none",
                borderRadius: 0,
                borderLeft: disableBorder ? "0 solid transparent" : `3px solid ${borderColor}`,
                display: "flex",
                flexDirection: "column",
                "& .MuiFormControl-root .Mui-disabled .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6e7891",
                },
                "& .MuiFormControl-root .Mui-disabled": {
                    opacity: .7,
                },
                "& .MuiFormControl-root .Mui-disabled .MuiInputBase-input": {
                    color: "#6e7891 !important",
                },
                "& .MuiFormControl-root .MuiFormLabel-root.Mui-disabled": {
                    color: "#6e7891",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    cursor: "pointer",
                }}
                onClick={() => setOpen((prev) => !prev)}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    {disableIcon ? null : resolvedIcon}
                    <Typography
                        variant="body2"
                        color="common.white"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                        }}
                    >
                        {title}
                    </Typography>
                    {header}
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    {actions && (
                        <Box
                            sx={{display: "flex", alignItems: "center", gap: 1}}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {actions}
                        </Box>
                    )}
                    {callbackFunction ?
                        <IconButton
                            size="small"
                            sx={{color: theme.palette.icon.main}}
                            onClick={(event) => {
                                event.stopPropagation()
                                callbackFunction(event)
                            }}
                        >
                            {iconCallbackFunction}
                        </IconButton> : null}
                    <IconButton
                        size="small"
                        sx={{color: theme.palette.icon.main}}
                        onClick={(event) => {
                            event.stopPropagation()
                            setOpen((prev) => !prev)
                        }}
                    >
                        <KeyboardArrowUpRoundedIcon
                            sx={{
                                transform: open ? "rotate(0deg)" : "rotate(180deg)",
                                transition: "transform 0.2s ease",
                            }}
                        />
                    </IconButton>
                </Box>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    transition:
                        "max-height 0.25s ease, opacity 0.25s ease, margin-top 0.25s ease, margin-bottom 0.25s ease",
                    maxHeight: open ? maxHeight : 0,
                    marginTop: open ? 2 : 0,
                    marginBottom: open ? 0 : 0,
                    opacity: open ? 1 : 0,
                    overflow: "hidden",
                }}
            >
                {children}
            </Box>
        </Paper>
    )
}

FormSectionAccordion.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    children: PropTypes.node.isRequired,
    actions: PropTypes.node,
    defaultOpen: PropTypes.bool,
    syncToken: PropTypes.number,
    isValid: PropTypes.bool,
    showError: PropTypes.bool,
    disableBorder: PropTypes.bool,
    header: PropTypes.element,
    disableIcon: PropTypes.bool,
    maxHeight: PropTypes.number,
    callbackFunction: PropTypes.func,
    iconCallbackFunction: PropTypes.func,
}

export default FormSectionAccordion
